(ns arthur.clock.graph
  "The clock on a Web Audio graph. No encode, no blob, no element.

  THE BUFFER IS PLAYED, NOT PACKAGED. `audio/mix`'s `buffer!` already renders
  the mixdown; the element backend then spent O(the clip's length) encoding that
  buffer to a WAV purely so a `src` attribute had something to point at. An
  `AudioBufferSourceNode` takes the buffer as it is, so opening a document costs
  one node instead of a hundred megabytes of 16-bit PCM.

  THE FRAME IS STILL DERIVED, AND FROM A BETTER CLOCK. `AudioContext.currentTime`
  is the audio device's own position in double precision, advancing every 128
  samples; an element's `currentTime` is whatever the media pipeline last
  published and is permitted to lag. Nothing is counted here either — position is
  an anchor plus elapsed context time, and the anchor is re-set on every seek and
  every rate change, so no arithmetic accumulates across either.

  AND IT IS LATENCY-COMPENSATED, which is the one thing this backend must get
  right. `currentTime` is the time of the quantum being RENDERED, which the
  speaker is `outputLatency` behind — tens of milliseconds, far more over
  Bluetooth. Report the renderer's position and the picture leads the sound by
  exactly that much, which in a lip-sync tool is the only artefact that matters.
  So `scheduled` is the bookkeeping and `-position` is `scheduled` read one
  latency in the past. The element has the same lag underneath; the difference is
  that this one is a number we can subtract rather than an error we inherit.

  A SYMBOL WITH NO SOUND GETS A CLOCK ANYWAY, with no buffer at all: `seconds`
  is its length and the context's clock does the rest. The element backend had to
  synthesize silence and encode THAT to a WAV, full length, so the element had a
  duration to report — the clearest sign that the element was driving the design
  rather than serving it."
  (:require [arthur.clock.transport :as t]))

(defonce ^:private shared (atom nil))

(defn available?
  "Whether this backend can run at all. False under node, where the tests live."
  []
  (exists? js/AudioContext))

(defn context
  "The app's one `AudioContext`, made on first use.

  Lazy because constructing one before anything wants to play is how a browser
  decides the page is trying to autoplay, and because `available?` is false in
  the test runner."
  []
  (or (:ctx @shared)
      (let [ctx (js/AudioContext.)
            ;; ONE output gain for the app, not one per backend: a tab switch
            ;; replaces the backend, and a gain node per backend would leave the
            ;; old one wired to the destination. Mute lives here for the same
            ;; reason — it is a property of the transport, not of whichever
            ;; buffer happens to be loaded.
            out (.createGain ctx)]
        (.connect out (.-destination ctx))
        (:ctx (reset! shared {:ctx ctx :out out})))))

(defn output [] (do (context) (:out @shared)))

(defn- latency
  "How far ahead of the speaker `currentTime` is, in seconds.

  `outputLatency` is the whole path and the number we want. Firefox reports 0
  until the graph has actually run, so `baseLatency` stands in — it is only the
  graph's own buffering and therefore an underestimate, which errs towards the
  picture leading slightly rather than the correction overshooting. Neither
  exists everywhere; 0 is then no worse than the element."
  [^js ctx]
  (let [out (.-outputLatency ctx)
        base (.-baseLatency ctx)]
    (cond
      (and (number? out) (js/isFinite out) (pos? out)) out
      (and (number? base) (js/isFinite base) (pos? base)) base
      :else 0)))

(defn- seg-at
  "The segment governing context time `t`."
  [segs t]
  (or (last (filter #(<= (:from %) t) segs)) (first segs)))

(defn- raw
  "Where the audio is at context time `t`, in seconds into the buffer.

  PIECEWISE, and that is the whole subtlety of this namespace. Audio already
  rendered cannot be re-rated: when the transport goes 1x -> 4x, the samples
  still travelling to the speaker were rendered at 1x, so reading them back at
  4x jumps the playhead BACKWARDS by three output latencies — about fourteen
  frames on a laptop, which is a visible lurch on every rate change and was
  exactly what the first version of this did. So each `play`, `pause`, `seek`
  and rate change records a segment, and a position is read against whichever
  segment was in force when that audio was rendered.

  Evaluated before the oldest segment we kept, it clamps. That is what makes a
  seek read back exactly what was seeked to: a seek has nothing in flight worth
  honouring — the user has jumped — so its segment starts the timeline over."
  [segs t]
  (let [t (max t (:from (first segs)))
        s (seg-at segs t)]
    (+ (:anchor s) (* (:rate s) (- t (:from s))))))

(defn- at-renderer
  "Where the graph has rendered up to: `raw` at the context's own clock."
  [{:keys [^js ctx segs]}]
  (raw segs (.-currentTime ctx)))

(defn- at-speaker
  "Where the sound being heard is: `raw` one output latency in the past. See the
  namespace docstring — this is the whole of the compensation."
  [{:keys [^js ctx segs]}]
  (raw segs (- (.-currentTime ctx) (latency ctx))))

(defn- running?
  "Whether the renderer is advancing. Read off the segments rather than kept
  beside them, so there is one answer and not two that can disagree."
  [{:keys [segs]}]
  (pos? (:rate (last segs))))

(defn- prune
  "Drop segments no position can still need: everything before the last one that
  began at or before the in-flight window. Unbounded history would otherwise
  grow by one entry per rate change for the life of the document."
  [segs cutoff]
  (let [n (count (take-while #(<= (:from %) cutoff) segs))]
    (if (<= n 1) segs (subvec segs (dec n)))))

(defn- position
  "`at-speaker`, brought inside the audio. Clamped before the wrap, so the few
  milliseconds of negative position right after a looped play — the first
  samples are still in the output buffer — read as 0 rather than as the end."
  [{:keys [seconds loop?] :as st}]
  (let [p (at-speaker st)]
    (cond
      (not (and seconds (pos? seconds))) 0
      loop? (mod (max 0 p) seconds)
      :else (-> p (max 0) (min seconds)))))

(defn- done?
  "Run off the end. Judged at the SPEAKER, so playback is still reported as
  running while the last scheduled samples are on their way out."
  [{:keys [seconds loop?] :as st}]
  (and (not loop?) seconds (pos? seconds) (>= (at-speaker st) seconds)))

(defn- spin-up!
  "A fresh source node playing from where the renderer now is.
  `AudioBufferSourceNode`s are single-use, so a play, a seek while playing and a
  resumed pause each make a new one; they are cheap, which is the point of this
  backend.

  Nil when there is no buffer — the silent clock runs on the context alone."
  [{:keys [^js ctx ^js buffer ^js out rate loop? seconds] :as st}]
  (when buffer
    (let [node (.createBufferSource ctx)]
      (set! (.-buffer node) buffer)
      (set! (.-loop node) (boolean loop?))
      (set! (.-value (.-playbackRate node)) rate)
      (.connect node out)
      ;; `when` 0 is "as soon as the graph can"; the offset is where in the
      ;; buffer to begin. Clamped because starting past the end is a range
      ;; error rather than a no-op in some engines.
      (.start node 0 (-> (at-renderer st) (max 0) (min (or seconds 0))))
      node)))

(defn- spin-down! [{:keys [^js node]}]
  (when node
    (try (.stop node) (catch :default _ nil))
    (try (.disconnect node) (catch :default _ nil))))

(defn- restart!
  "Begin a new timeline at `anchor`, running at `rate` or held when it is 0.

  A RESET rather than a segment, for the three cases that have nothing in flight
  worth honouring: a play starts fresh, a seek means the user has jumped, and a
  pause wants one stable number for the readout rather than a position that
  creeps forward as the output buffer drains."
  [state anchor rate]
  (let [st @state]
    (spin-down! st)
    (swap! state assoc
           :segs [{:from (.-currentTime ^js (:ctx st)) :anchor anchor :rate rate}]
           :node nil)
    (when (pos? rate)
      (swap! state assoc :node (spin-up! @state)))))

(deftype Graph [state]
  t/Transport
  (-position [_] (position @state))
  (-duration [_] (:seconds @state))
  (-playing? [_] (let [st @state] (boolean (and (running? st) (not (done? st))))))
  (-rate [_] (:rate @state))

  (-set-rate! [_ r]
    ;; A SEGMENT, not a reset — the one case where what is already in flight has
    ;; to keep its old rate or the playhead lurches. See `raw`.
    (let [st @state
          now (.-currentTime ^js (:ctx st))]
      (if (running? st)
        (let [anchor (at-renderer st)]
          (swap! state #(-> %
                            (assoc :rate r)
                            (update :segs (fn [segs]
                                            (prune (conj segs {:from now :anchor anchor :rate r})
                                                   (- now (latency ^js (:ctx st))))))))
          (when-let [^js node (:node st)]
            (set! (.-value (.-playbackRate node)) r)))
        ;; Stopped: nothing is in flight and nothing is rendering, so the rate
        ;; is simply what the next play will run at.
        (swap! state assoc :rate r))))

  (-play! [_]
    (let [st @state]
      ;; `done?` counts as not playing. It is DERIVED — no flag is cleared when
      ;; the take runs off its end — so testing "running" alone would make play
      ;; a dead button from the moment the audio finished.
      (when (or (not (running? st)) (done? st))
        ;; The context starts suspended and only a gesture may resume it. The
        ;; transport button IS the gesture, so a rejection here means a
        ;; programmatic play and is noise rather than news — as on the element.
        (some-> (.resume ^js (:ctx st)) (.catch (fn [_])))
        ;; Play at the end starts over, which is what the element does.
        (restart! state (if (done? st) 0 (position st)) (:rate st)))))

  (-pause! [_]
    (let [st @state]
      (when (running? st)
        ;; Anchored at what was HEARD, not at what was scheduled, so play after
        ;; pause resumes from where the sound stopped. It re-plays the last few
        ;; milliseconds rather than skipping them, which is the kinder of the
        ;; two roundings.
        (restart! state (position st) 0))))

  (-seek! [_ seconds]
    (restart! state seconds (if (running? @state) (:rate @state) 0)))

  (-set-loop! [_ on?]
    (swap! state assoc :loop? (boolean on?))
    ;; The audio thread reads `loop` every quantum, so a live node picks this up
    ;; without being restarted — the same as setting `loop` on a playing element.
    (when-let [^js node (:node @state)]
      (set! (.-loop node) (boolean on?))))

  (-set-muted! [_ on?]
    (set! (.-value (.-gain ^js (:out @state))) (if on? 0 1)))

  (-release! [_]
    ;; The source node is wired to the output the whole app shares, so a backend
    ;; dropped while playing would go on being heard under the one that replaced
    ;; it. Nothing else here needs releasing: the context and its gain outlive
    ;; every backend by design.
    (let [st @state]
      (spin-down! st)
      (swap! state assoc
             :segs [{:from (.-currentTime ^js (:ctx st))
                     :anchor (position st) :rate 0}]
             :node nil))))

(defn backend
  "A clock on `buffer`, `seconds` long. A nil buffer is a silent clock of that
  length — see the namespace docstring.

  The context and output are injected by the four-argument form so the whole of
  this is assertable against a fake in node, where there is no Web Audio."
  ([buffer seconds] (backend (context) (output) buffer seconds))
  ([^js ctx ^js out buffer seconds]
   (->Graph (atom {:ctx ctx :out out :buffer buffer :seconds seconds
                   :rate 1.0 :loop? false :node nil
                   :segs [{:from (.-currentTime ctx) :anchor 0 :rate 0}]}))))
